import React, { useState, useRef } from 'react';
import { Calendar } from 'primereact/calendar';
import { Nullable } from 'primereact/ts-helpers';
import { CircleAlert, Calendar as CalendarIcon } from 'lucide-react';
import { C, G } from './uiTokens';

interface GlobalDateFieldProps {
  label?: string;
  value: Nullable<Date | Date[] | (Date | null)[]>;
  onChange: (value: Nullable<Date | Date[] | (Date | null)[]>) => void;
  onBlur?: () => void;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  hint?: string;
  /** red border without a message (e.g. when the error is shown elsewhere) */
  invalid?: boolean;
  /** content aligned to the right of the label */
  labelRight?: React.ReactNode;
  fullWidth?: boolean;
  showTime?: boolean;
  timeOnly?: boolean;
  dateFormat?: string;
  minDate?: Date;
  maxDate?: Date;
  showButtonBar?: boolean;
  selectionMode?: 'single' | 'multiple' | 'range';
  inline?: boolean;
  monthNavigator?: boolean;
  yearNavigator?: boolean;
  yearRange?: string;
  name?: string;
  id?: string;
  /** hide the leading calendar icon */
  hideIcon?: boolean;
}

export const GlobalDateField: React.FC<GlobalDateFieldProps> = ({
  label,
  value,
  onChange,
  onBlur,
  placeholder = 'dd/mm/yyyy',
  required = false,
  disabled = false,
  error = '',
  hint,
  invalid = false,
  labelRight,
  fullWidth = true,
  showTime = false,
  timeOnly = false,
  dateFormat = 'dd/mm/yy',
  minDate,
  maxDate,
  showButtonBar = false,
  selectionMode = 'single',
  inline = false,
  monthNavigator = false,
  yearNavigator = false,
  yearRange = '2000:2030',
  name,
  id,
  hideIcon = false,
}) => {
  const [focused, setFocused] = useState(false);
  const calendarRef = useRef<Calendar>(null);

  const hasError = !!error || invalid;
  const borderColor = hasError ? C.danger : focused ? 'rgba(17,85,80,0.5)' : G.line;
  const focusRing = hasError ? 'rgba(220,38,38,0.1)' : 'rgba(17,85,80,0.08)';

  const handleChange = (e: { value: Nullable<Date | Date[] | (Date | null)[]> }) => {
    onChange(e.value);
  };

  const handleWrapperClick = () => {
    if (disabled || inline) return;
    // Input may already be focused (e.g. right after a date was picked),
    // in which case the native focus event won't refire on the next click
    // and the overlay would otherwise never reopen. Force it open explicitly.
    calendarRef.current?.show();
  };

  return (
    <div style={{ width: fullWidth ? '100%' : 'auto' }}>
      {label && (
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            marginBottom: '5.9px',
          }}
        >
          <label
            htmlFor={id}
            style={{
              display: 'block',
              fontSize: '0.73rem',
              fontWeight: 600,
              color: hasError ? C.danger : '#374151',
            }}
          >
            {label}
            {required && <span style={{ color: '#ef4444', marginLeft: '3px' }}>*</span>}
          </label>
          {labelRight}
        </div>
      )}

      <div style={{ position: 'relative' }} onClick={handleWrapperClick}>
        {!hideIcon && !inline && (
          <span
            style={{
              position: 'absolute',
              left: '11.8px',
              top: '50%',
              transform: 'translateY(-50%)',
              display: 'flex',
              pointerEvents: 'none',
              color: focused ? C.teal : C.muted,
              transition: 'color 0.18s',
              zIndex: 1,
            }}
          >
            <CalendarIcon size={15} />
          </span>
        )}

        <Calendar
          ref={calendarRef}
          inputId={id}
          name={name}
          value={value}
          onChange={handleChange}
          onFocus={() => setFocused(true)}
          onBlur={() => {
            setFocused(false);
            onBlur?.();
          }}
          placeholder={placeholder}
          disabled={disabled}
          showTime={showTime}
          timeOnly={timeOnly}
          dateFormat={dateFormat}
          minDate={minDate}
          maxDate={maxDate}
          showButtonBar={showButtonBar}
          showIcon={false}
          selectionMode={selectionMode}
          inline={inline}
          monthNavigator={monthNavigator}
          yearNavigator={yearNavigator}
          yearRange={yearRange}
          style={{ width: '100%' }}
          appendTo={inline ? undefined : document.body}
          panelClassName="global-date-panel"
          className="global-date-field"
          inputClassName="global-date-input"
        />
      </div>

      {error ? (
        <span
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4.3px',
            marginTop: '5.4px',
            fontSize: '0.71rem',
            color: C.danger,
            fontWeight: 500,
          }}
        >
          <CircleAlert size={12} /> {error}
        </span>
      ) : hint ? (
        <span
          style={{
            display: 'block',
            marginTop: '5.4px',
            fontSize: '0.71rem',
            color: C.sub,
          }}
        >
          {hint}
        </span>
      ) : null}

      <style>{`
        .global-date-field.p-calendar,
        .global-date-field .p-calendar {
          width: 100%;
        }

        .global-date-field .p-inputtext,
        .global-date-input {
          width: 100% !important;
          box-sizing: border-box !important;
          height: 38.7px !important;
          padding: 0 11.8px 0 ${hideIcon || inline ? '11.8px' : '35.5px'} !important;
          border: 1px solid ${borderColor} !important;
          border-radius: 8.9px !important;
          font-size: 0.83rem !important;
          font-weight: 500 !important;
          font-family: inherit !important;
          background: ${disabled ? '#f9fafb' : 'white'} !important;
          color: ${disabled ? C.muted : '#1f2937'} !important;
          outline: none !important;
          box-shadow: ${focused ? `0 0 0 3px ${focusRing}` : 'none'} !important;
          transition: border-color 0.18s, box-shadow 0.18s !important;
          cursor: ${disabled ? 'not-allowed' : 'text'} !important;
        }

        .global-date-field .p-inputtext::placeholder,
        .global-date-input::placeholder {
          color: rgba(17,85,80,0.3) !important;
          opacity: 1;
        }

        .global-date-field .p-inputtext:enabled:hover,
        .global-date-input:enabled:hover {
          border-color: ${hasError ? C.danger : 'rgba(17,85,80,0.3)'} !important;
        }

        .global-date-field .p-inputtext:enabled:focus,
        .global-date-input:enabled:focus {
          border-color: ${borderColor} !important;
          box-shadow: 0 0 0 3px ${focusRing} !important;
        }

        /* ── Panel (portaled to document.body, so styled as a top-level class) ── */
        .global-date-panel.p-datepicker {
          background: white !important;
          border: 1px solid ${G.line} !important;
          border-radius: 12px !important;
          box-shadow: 0 10px 40px rgba(17,85,80,0.15) !important;
          padding: 12px;
          font-family: inherit;
          z-index: 9999 !important;
          min-width: 300px;
        }

        .global-date-panel .p-datepicker-header {
          padding: 10px 12px;
          background: linear-gradient(135deg, rgba(17,85,80,0.04) 0%, rgba(17,85,80,0.02) 100%);
          border-radius: 8px 8px 0 0;
          border-bottom: 1px solid ${G.line};
        }

        .global-date-panel .p-datepicker-header .p-datepicker-title {
          margin: 0;
          color: ${C.teal};
          font-weight: 700;
          font-size: 0.85rem;
        }

        .global-date-panel .p-datepicker-header .p-datepicker-prev,
        .global-date-panel .p-datepicker-header .p-datepicker-next {
          color: ${C.teal};
          opacity: 0.55;
          width: 30px;
          height: 30px;
          border-radius: 8px;
          transition: all 0.15s ease;
        }

        .global-date-panel .p-datepicker-header .p-datepicker-prev:hover,
        .global-date-panel .p-datepicker-header .p-datepicker-next:hover {
          background: rgba(17,85,80,0.08);
          color: ${C.teal};
          opacity: 1;
        }

        .global-date-panel table td {
          padding: 3px;
        }

        .global-date-panel table td > span {
          width: 34px;
          height: 34px;
          border-radius: 8px;
          transition: all 0.15s ease;
          color: #333;
          font-size: 0.81rem;
          font-weight: 500;
        }

        .global-date-panel table td > span:hover {
          background: rgba(17,85,80,0.08);
          color: ${C.teal};
        }

        .global-date-panel table td > span.p-highlight {
          background: linear-gradient(135deg, ${C.teal} 0%, ${C.tealMid ?? C.teal} 100%);
          color: white;
          font-weight: 700;
          box-shadow: 0 3px 10px rgba(17,85,80,0.28);
        }

        .global-date-panel table td.p-datepicker-today > span {
          background: rgba(200,233,75,0.22);
          color: ${C.teal};
          font-weight: 700;
          border: 1px solid rgba(200,233,75,0.5);
        }

        .global-date-panel table td.p-datepicker-today > span.p-highlight {
          background: linear-gradient(135deg, ${C.teal} 0%, ${C.tealMid ?? C.teal} 100%);
          color: white;
          border: none;
        }

        .global-date-panel .p-monthpicker .p-monthpicker-month,
        .global-date-panel .p-yearpicker .p-yearpicker-year {
          border-radius: 8px;
          transition: all 0.15s ease;
        }

        .global-date-panel .p-monthpicker .p-monthpicker-month:hover,
        .global-date-panel .p-yearpicker .p-yearpicker-year:hover {
          background: rgba(17,85,80,0.08);
          color: ${C.teal};
        }

        .global-date-panel .p-monthpicker .p-monthpicker-month.p-highlight,
        .global-date-panel .p-yearpicker .p-yearpicker-year.p-highlight {
          background: linear-gradient(135deg, ${C.teal} 0%, ${C.tealMid ?? C.teal} 100%);
          color: white;
          font-weight: 700;
        }

        .global-date-panel .p-timepicker {
          border-top: 1px solid ${G.line};
          padding: 10px 12px;
          margin-top: 6px;
        }

        .global-date-panel .p-timepicker button {
          color: ${C.teal};
          opacity: 0.55;
          width: 30px;
          height: 30px;
          border-radius: 8px;
          transition: all 0.15s ease;
        }

        .global-date-panel .p-timepicker button:hover {
          background: rgba(17,85,80,0.08);
          color: ${C.teal};
          opacity: 1;
        }

        .global-date-panel .p-datepicker-buttonbar {
          padding: 10px 12px;
          border-top: 1px solid ${G.line};
          background: linear-gradient(135deg, rgba(17,85,80,0.04) 0%, rgba(17,85,80,0.02) 100%);
          border-radius: 0 0 8px 8px;
        }

        .global-date-panel .p-datepicker-buttonbar button {
          padding: 7px 14px;
          border-radius: 8px;
          font-size: 0.81rem;
          font-weight: 600;
          transition: all 0.15s ease;
        }

        .global-date-panel .p-datepicker-buttonbar .p-button-text {
          color: ${C.sub};
        }

        .global-date-panel .p-datepicker-buttonbar .p-button-text:hover {
          background: rgba(17,85,80,0.08);
          color: ${C.teal};
        }

        .global-date-field.p-calendar-disabled .p-inputtext,
        .global-date-field .p-calendar.p-calendar-disabled .p-inputtext {
          opacity: 0.7;
          cursor: not-allowed;
        }

        /* Inline mode still lives inside the wrapper, so it keeps the nested selector */
        .global-date-field .p-datepicker-inline {
          border: 1px solid ${G.line};
          border-radius: 12px;
          box-shadow: 0 4px 16px rgba(17,85,80,0.08);
        }

        .global-date-panel table th.p-datepicker-weeknumber,
        .global-date-panel table td.p-datepicker-weeknumber {
          color: ${C.muted};
          font-weight: 500;
          font-size: 0.72rem;
        }

        .global-date-panel table td.p-disabled > span {
          opacity: 0.3;
          cursor: not-allowed;
        }

        .global-date-panel table td.p-datepicker-other-month > span {
          color: #ccc;
        }
      `}</style>
    </div>
  );
};